import { useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

export default function HeroSection() {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-bg-tertiary">
      {/* Background Image Container */}
      <div className="absolute inset-0">
        {/* Fallback gradient background - shows if image fails */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8E6E1] to-[#D4D2CD]" />

        {/* Hero Image */}
        {!imageError && (
          <img
            src="/images/hero-main.png"
            alt="Elegant nails"
            className={cn(
              "absolute inset-0 w-full h-full object-cover object-center z-[1]",
              imageLoaded ? "opacity-100" : "opacity-0",
              "transition-opacity duration-500"
            )}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}

        {/* Gradient overlay for text readability - on top of image */}
        <div className="absolute inset-0 bg-gradient-to-r from-bg-tertiary/90 via-bg-tertiary/60 to-transparent z-[2]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="max-w-xl">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-text-secondary mb-4"
          >
            Smart Beauty Technology
          </motion.p>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-display-lg font-serif font-medium text-text-primary mb-6"
          >
            The Future of<br />
            <span className="italic">Nail Art</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base text-text-secondary mb-8 max-w-md"
          >
            Experience premium press-on nails with integrated NFC technology.
            Tap to pay, share contacts, and express your style.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a
              href="#products"
              className={cn(
                'inline-flex items-center justify-center',
                'px-8 py-4',
                'bg-primary text-white',
                'text-sm font-medium uppercase tracking-wider',
                'hover:bg-primary-light transition-colors duration-300'
              )}
            >
              Shop Now
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <span className="text-xs uppercase tracking-widest text-text-muted">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-px h-8 bg-text-muted/50"
        />
      </motion.div>
    </section>
  )
}
